<template>
  <StepItem
    format="form"
    title="Setup a company admin account"
    subtitle="This page is encrypted for your security | Step 2 of 3"
    :loading="loading"
    show-lock
    can-go-to-prev-step
    @handle-prev-step="handlePrevStep"
    @handle-next-step="handleNextStep"
    :is-ready-for-next-step="isReadyForNextStep"
  >
    <div v-if="!store.useExistingAccountAsAdmin">
      <div class="mb-4 flex items-center justify-between gap-4 md:gap-6">
        <FormInput
          class="w-full"
          required
          label="First Name"
          autocomplete="given-name"
          placeholder="John"
          v-model="store.firstName"
          :error="store?.errors?.firstName"
          @update:modelValue="handleUpdateName"
        />
        <FormInput
          class="w-full"
          required
          label="Last Name"
          autocomplete="family-name"
          placeholder="Doe"
          v-model="store.lastName"
          :error="store?.errors?.lastName"
          @update:modelValue="handleUpdateName"
        />
      </div>
      <div class="mb-6 flex items-center justify-between gap-4 md:gap-6">
        <FormInput
          class="w-full"
          required
          label="Email Address"
          type="email"
          input-mode="email"
          placeholder="john@example.com"
          v-model="store.email"
          :error="store?.errors?.email"
          @update:modelValue="throttleValidateCurrentStep"
        />
        <FormInput
          class="w-full"
          required
          label="Phone Number"
          format="phone"
          type="tel"
          placeholder="(123) 555-5555"
          v-model="store.phone"
          :error="store?.errors?.phone"
          @update:modelValue="throttleValidateCurrentStep"
          autocomplete="tel"
        />
      </div>
      <hr class="my-3 bg-slate-100"/>
      <FormInput
        class="w-full"
        required
        label="Username"
        format="username"
        placeholder="@username"
        v-model="store.username"
        :error="store?.errors?.username"
        @update:modelValue="throttleValidateCurrentStep"
      />
      <p class="mt-2 mb-6 text-xs text-slate-400">Usernames must be 5-30 characters, and only have only letters,
        numbers, or periods.</p>
      <hr class="my-3 bg-slate-100"/>
      <div class="mt-7 flex items-center justify-between gap-4 md:gap-6">
        <FormInput
          class="w-full"
          required
          label="Company Admin Password"
          type="password"
          autocomplete="new-password"
          placeholder="••••••••"
          v-model="store.password"
          :error="store?.errors?.password"
          @update:modelValue="throttleValidateCurrentStep"
        />
        <FormInput
          class="w-full"
          required
          label="Confirm Company Admin Password"
          type="password"
          autocomplete="new-password"
          placeholder="••••••••"
          v-model="store.passwordConfirmation"
          :error="store?.errors?.passwordConfirmation"
          @update:modelValue="throttleValidateCurrentStep"
        />
      </div>
      <FormPasswordValidator
        :password="store.password"
        :password-confirmation="store.passwordConfirmation"
      />
      <button type="button" @click="store.useExistingAccountAsAdmin=!store.useExistingAccountAsAdmin"
              class="mt-6 cursor-pointer text-sm text-slate-400 underline hover:text-slate-900">
        Or Login to Existing Account
      </button>
    </div>
    <div v-else>
      <div class="flex items-center justify-between gap-4 md:gap-6">
        <FormInput
          label="Email Address"
          placeholder="Email address"
          required
          v-model="store.existingEmail"
          type="email"
          input-mode="email"
          class="w-full sm:w-96"
          @update:modelValue="throttleValidateCurrentStep"
        />
        <FormInput
          label="Password"
          placeholder="••••••••"
          required
          v-model="store.existingPassword"
          type="password"
          class="w-full sm:w-96"
          @update:modelValue="throttleValidateCurrentStep"
        />
      </div>
      <div v-if="twoFactor.required" class="space-y-6 mb-24 mt-12">
        <TwoFactorChallenge v-model:code="twoFactor.code" v-model:recovery-code="twoFactor.recoveryCode"/>
        <div class="mt-6 mb-96 flex items-center justify-center gap-4">
          <FormButton type="button" size="full" color="gradient" :loading="twoFactor.loading" @click="handleTwoFactor">
            Submit Code
          </FormButton>
        </div>
      </div>
      <button type="button"
              @click="store.useExistingAccountAsAdmin = !store.useExistingAccountAsAdmin"
              class="mt-4 cursor-pointer text-sm text-slate-400 underline hover:text-slate-900">
        Or Create New Account
      </button>
    </div>
  </StepItem>
</template>

<script setup>
import StepItem from "./StepItem.vue";
import FormInput from "./FormInput.vue";
import FormPasswordValidator from "./FormPasswordValidator.vue";
import {onActivated, onMounted, reactive, ref, watch} from "vue";
import {useCompanyAccountCreateStore} from "../stores/CompanyAccountCreateStore.js";
import {throttle} from "../helpers/DebounceAndThrottle.js";
import {useAccountStore} from "../stores/AccountStore.js";
import {checkIfEmailExists, checkIfUsernameTaken} from "../helpers/CommonAccountFunctions.js";
import {debounce} from "lodash";
import TwoFactorChallenge from "./TwoFactorChallenge.vue";
import FormButton from "./FormButton.vue";
import {doubleCheckIsLoggedIn} from "../helpers/Redirects.js";

const store = useCompanyAccountCreateStore();
const accountStore = useAccountStore();
const isReadyForNextStep = ref(false);
const loading = ref(false);
const emits = defineEmits(["complete", "prev-step"]);

const twoFactor = reactive({
  required: false,
  loading: false,
  code: "",
  recoveryCode: "",
});

const handleNextStep = async () => {
  if (!isReadyForNextStep.value || loading.value) return;
  loading.value = true;

  try {
    if (store.useExistingAccountAsAdmin) {
      if (accountStore.isLoggedIn) {
        await accountStore.logout();
      }

      await accountStore
        .login(
          store.existingEmail,
          store.existingPassword,
          true
        )
        .then(() => {
          loading.value = false;
          if (accountStore.isLoggedIn) emits("complete", 2);
          else twoFactor.required = true;
        });
    } else {
      if (await handleCheckIfEmailExists()) return loading.value = false;
      if (await handleCheckIfUsernameTaken()) return loading.value = false;
      emits("complete", 1);
    }
  } finally {
    loading.value = false;
  }
};

const handleTwoFactor = () => {
  twoFactor.loading = true;
  accountStore.twoFactorChallenge({code: twoFactor.code, recoveryCode: twoFactor.recoveryCode})
    .then(() => emits("complete", 2))
    .finally(() => twoFactor.loading = false);
};

const handlePrevStep = () => emits("prev-step", 2);

const handleCheckIfEmailExists = async () => {
  const exists = await checkIfEmailExists(store.email);

  if (exists) {
    store.errors.email = "Email already exists";
    isReadyForNextStep.value = false;
  }

  return exists;
};

const handleCheckIfUsernameTaken = async () => {
  const status = await checkIfUsernameTaken(store.username);

  if (status.taken) toast.error("That username is already taken. Please try another one.");

  return status.taken || !status.valid || !!status.reason;
};

const handleUpdateName = debounce(() => {
  const firstName = store.firstName.replace(/[^a-zA-Z]/g, "");
  const lastName = store.lastName.replace(/[^a-zA-Z]/g, "");

  const separator = firstName && lastName ? "." : "";

  store.username = `${firstName}${separator}${lastName}`.toLowerCase();
  validateCurrentStep();
});

const validateCurrentStep = async (showToast = false) =>
  (isReadyForNextStep.value =
    await store.validateStep4(showToast));
const throttleValidateCurrentStep = () => throttle(() => validateCurrentStep(false), 300);

watch(() => store.useExistingAccountAsAdmin, () => {
  if (store.useExistingAccountAsAdmin) {
    store.username = "";
    store.email = "";
  } else {
    store.existingEmail = "";
    store.existingPassword = "";
  }
});

const handleLoggedInAccount = () => {
  if (!accountStore.isLoggedIn) return;

  toast.success("You are already logged in! No need to enter your account details.");
  store.existingEmail = accountStore.user.email;
  store.useExistingAccountAsAdmin = true;
  emits("complete", 2);
};

const init = async () => {
  await doubleCheckIsLoggedIn();
  handleLoggedInAccount();
  await validateCurrentStep(false);
};

onActivated(async () => await init());
onMounted(async () => await init());
</script>
